import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import { SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR } from 'api/actionTypes';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import { PARTNER_ID_HOMERIVER } from 'common/partnerConstants';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_TYPE_CODEBOX_HOMERIVER,
  INVENTORY_ITEM_TYPE_CODEBOX_SUNROOM,
} from 'common/inventoryConstants';

import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const DRIVER_CODEBOX_STATE_INITIAL = 'initial';
const DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET = 'serialNumberSet';

const getFormFields = ({ setIsBarcodeScannerOpen, setFocusedField, isCodeboxTakedown }) => {
  return [
    {
      id: 'codeboxSerialNumber',
      title: 'CodeBox Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('raspberryPiSerialNumber');
        },
      },
    },
    showFieldIf(isCodeboxTakedown, {
      id: 'codeboxStatus',
      title: 'CodeBox Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
  ];
};

const SrSunSetupDriverJobCodeboxBarcode = ({
  partnerId,
  codeboxLock,
  formikBag,
  itemScans,
  listingAssignedItems,
  isCodeboxTakedown,
  requestGeolocationPermission,
  getCurrentLocation,
  handleCodeBoxRegistration,
}) => {
  const [viewState, setViewState] = useState(
    codeboxLock || itemScans.length > 0
      ? DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET
      : DRIVER_CODEBOX_STATE_INITIAL,
  );
  const [codeboxMissingError, setCodeboxMissingError] = useState(false);
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();

  const CODEBOX_ITEM_TYPE =
    partnerId === PARTNER_ID_HOMERIVER
      ? INVENTORY_ITEM_TYPE_CODEBOX_HOMERIVER
      : INVENTORY_ITEM_TYPE_CODEBOX_SUNROOM;

  const _onCodeboxLock = (codeboxLock) => {
    if (partnerId !== PARTNER_ID_HOMERIVER) {
      setCodeboxMissingError(!codeboxLock);
    }
    setViewState(
      codeboxLock || itemScans.length > 0
        ? DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET
        : DRIVER_CODEBOX_STATE_INITIAL,
    );
  };

  const _onSubmit = async (formValues) => {
    const location = await _requestGeolocationPermission();
    const { latitude, longitude } = location;

    const { codeboxSerialNumber, codeboxStatus } = formValues;

    handleCodeBoxRegistration({
      type: CODEBOX_ITEM_TYPE,
      serialNumber: codeboxSerialNumber,
      latitude,
      longitude,
      ...(isCodeboxTakedown && { status: codeboxStatus }),
    }).then((codeboxLock) => {
      return _onCodeboxLock(codeboxLock);
    });
  };

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);
    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();

      setIsLocationLoading(false);
      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });
      setIsLocationLoading(false);
      throw error;
    }
  };

  const _onFieldScan = ({ barcode, formikBag }) => {
    if (!focusedField) {
      return;
    }
    formikBag.setFieldValue(focusedField, barcode);
    setFocusedField(null);
    setIsBarcodeScannerOpen(false);
  };

  const _handleScannerClose = () => {
    setIsBarcodeScannerOpen(false);
    setFocusedField(null);
  };

  const getItemScansSerialNumberMapping = () => {
    const itemScansSerialNumberMapping = {};
    itemScans.forEach((itemScan) => {
      itemScansSerialNumberMapping[itemScan.inventoryItemTypeId] = {
        serialNumber: itemScan.serialNumber,
        status: itemScan.status,
      };
    });
    return itemScansSerialNumberMapping;
  };

  const itemScansSerialNumberMapping = getItemScansSerialNumberMapping();

  const initialValues = {
    codeboxSerialNumber:
      listingAssignedItems[CODEBOX_ITEM_TYPE]?.serialNumber ||
      itemScansSerialNumberMapping[CODEBOX_ITEM_TYPE]?.serialNumber ||
      codeboxLock?.serialNumber ||
      null,
    codeboxStatus:
      listingAssignedItems[CODEBOX_ITEM_TYPE]?.status ||
      itemScansSerialNumberMapping[CODEBOX_ITEM_TYPE]?.status ||
      codeboxLock?.status ||
      null,
  };

  return (
    <div styleName="card" className="fieldsContainer" id="field-codeboxLockId">
      <span styleName="cardTitle">Register Codebox</span>

      <SrBox margin="16px 0 0">
        {viewState === DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET && (
          <SrBox styleName="jobDetailsBox">
            <SrBox styleName="title">CodeBox Barcode Number</SrBox>
            {itemScansSerialNumberMapping[CODEBOX_ITEM_TYPE]?.status && (
              <SrBox styleName="status">
                Status:&nbsp;
                {itemScansSerialNumberMapping[CODEBOX_ITEM_TYPE]?.status}
              </SrBox>
            )}
            <SrBox styleName="serialNumber">
              {itemScansSerialNumberMapping[CODEBOX_ITEM_TYPE]?.serialNumber ||
                codeboxLock?.serialNumber ||
                '--'}
            </SrBox>
            <SrBox margin="16px 0 0">
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_CODEBOX_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </SrBox>
        )}
        {viewState === DRIVER_CODEBOX_STATE_INITIAL && (
          <Formik
            onSubmit={_onSubmit}
            initialValues={createInitialValues({
              formFields: getFormFields({
                setIsBarcodeScannerOpen,
                setFocusedField,
                isCodeboxTakedown,
              }),
              defaultValues: initialValues,
            })}
            validate={() =>
              createFormValidationFn(
                getFormFields({
                  setIsBarcodeScannerOpen,
                  setFocusedField,
                  isCodeboxTakedown,
                }),
              )
            }
            render={(formikBag) => {
              const formFields = getFormFields({
                setFocusedField,
                setIsBarcodeScannerOpen,
                isCodeboxTakedown,
              });
              return (
                <Form>
                  {formFields.map((formField) => (
                    <>
                      <SrFormikField
                        formikBag={formikBag}
                        formField={formField}
                        theme={SR_FIELD_THEME_ADOBE}
                        key={formField.id}
                      />
                      {codeboxMissingError && formField.id === 'codeboxSerialNumber' && (
                        <SrBox padding="0 0 16px">
                          <SrErrorMessage>
                            A CodeBox with this Barcode Number was not found. Please try again.
                          </SrErrorMessage>
                        </SrBox>
                      )}
                    </>
                  ))}
                  {isBarcodeScannerOpen && (
                    <SrBarcodeScanner
                      onText={(barcode) => _onFieldScan({ barcode, formikBag })}
                      onClose={_handleScannerClose}
                    />
                  )}
                  <ButtonDeprecated
                    margin="24px 0 0"
                    type="submit"
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                    disabled={isLocationLoading}
                  >
                    {isLocationLoading ? 'Loading...' : 'Submit'}
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />
        )}
      </SrBox>
      <SrFormikErrorMessage id="codeboxLockId" formikBag={formikBag} />
    </div>
  );
};

SrSunSetupDriverJobCodeboxBarcode.propTypes = {
  codeboxLock: PropTypes.object,
  formikBag: PropTypes.object.isRequired,
  itemScans: PropTypes.array.isRequired,
  listingAssignedItems: PropTypes.array.isRequired,
  isCodeboxTakedown: PropTypes.bool.isRequired,
  handleCodeBoxRegistration: PropTypes.func.isRequired,
  requestGeolocationPermission: PropTypes.func.isRequired,
  getCurrentLocation: PropTypes.func.isRequired,
};

SrSunSetupDriverJobCodeboxBarcode.displayName = 'SrSunSetupDriverJobCodeboxBarcode';

export default SrSunSetupDriverJobCodeboxBarcode;
